import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { Adjudicator, Dance, Heat, Round, RoundParticipant } from './workSlice';
import { StyleType } from '../constants/style';
import { RankingSystemType } from '../constants/rankingSystems';

export interface TimetableItem {
  time: string;
  expectedMinutes: string | number;
  text: string;
  round: string;
  dances: string;
  heats: number | null;
  participants: string;
  ttUuid: string;
  adjudicators?: string[];
  wdsfId?: string | null;
  categoryColor?: string | null;
  floor?: 'one' | 'A' | 'B';
  isJoinedTo?: string;
  hall?: number; // Hall number this timetable item belongs to
  delay?: number;
  baselineStartTime?: string;
  actualFinishTime?: string;
  isFixedTime?: boolean;
}

export interface TtItem {
  competitionUuid: string;
  floorUuid: string;
  text: string;
  prefix: string;
  roundUuid: string;
  categoryUuid: string;
  blockUuid: string;
  dances: Dance[];
  startTime: string;
  endTime: string;
  expectedMinutes: number;
  disabled: boolean;
  itemType: string;
  index: number;
  tempIndex: number;
  uuid: string;
  style: string;
  heatsNumber: number;
  soloDanceTime: number;
  groupDanceTime: number;
  expectedParticipants: number;
  status: {
    sheetsSend: boolean;
    startlistPrinted: boolean;
    heatsSentToMonitor: boolean;
  };
}

export interface FinalParticipantResult {
  uuid: string;
  entryUuid: string;
  startNo: number;
  name: string;
  roundUuid: string;
  roundName: string;
  finishedPlace: number | null;
  finalRank: number;
  notStartedReason: null | string;
  notFinished: boolean;
  notFinishedReason: null | 'disqualified' | 'resigned' | 'health';
  disqualificationType?: 'last_in_round' | 'last_overall';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details: any;
}

export interface TimetableDetails {
  ttUuid: string;
  adjudicators: Adjudicator[];
  chairperson?: Adjudicator | null;
  dj?: Adjudicator | null;
  heats: Heat[];
  participants: RoundParticipant[];
  ttItem: TtItem;
  currentRound: Round;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  results: any | null;
  /**
   * Per-participant SUM map (entryUuid -> total) computed by the main app for
   * AJS24/AJS26/SKATING/AJS_SHOWDANCE/S_AJS/MARKS systems. Mirrors the `Sum`
   * value used in main-app LED broadcasts so the mobile app can display the
   * same totals consistently.
   */
  sums?: Record<string, number> | null;
  finalRanking?: FinalParticipantResult[] | null;
  receivedAt?: number; // Timestamp when details were received
}
export interface Competition {
  _rev?: string;
  _id?: string;
  datetime: string;
  id: string;
  competitionName: string;
  city: City;
  country?: string;
  venues: Venue[];
  dates: string;
  organizer: Organizer[];
  chairpeople: Adjudicator[];
  judges: Adjudicator[];
  scrutineers: Adjudicator[];
  djs?: Adjudicator[];
  blocks: Block[];
  blocksAll: Block[];
  categories: Category[];
  events: never[];
  eventsAll: never[];
  onlyNational: boolean;
  dateStart: string;
  links: Link[];
  regUrl: string;
  websiteUrl: string;
  numbering: Numbering;
  type: string;
  subtype: string;
  liveRes: LiveRes;
  status: Status;
  uuid: string;
  hallsNumber?: number; // Number of halls (default 1)
  hallNames?: Record<number, string>; // Custom names for halls
}
export interface Block {
  id: string;
  uuid: string;
  datetime: string;
  endsAt: string;
  name: string;
  number: number;
  rehearsalAt: string;
  startsAt: string;
  type: string;
  competitionUuid: string;
}
export interface City {
  datetime: string;
  id: string;
  name: string;
  zipCode?: string;
}
export interface Link {
  datetime: string;
  id: string;
  link: string;
  isPdf: boolean;
}
export interface Venue {
  datetime: string;
  type: string;
  id: string;
  name: string;
  city: City;
  address: string;
}

export interface Organizer {
  datetime: string;
  type: string;
  id: string;
  name: string;
  shortName: string;
  city: string;
  email: string;
  phone: string;
}
export interface Numbering {
  lastNo: number;
  noSeparateForEachDay: boolean;
  noOnePerCouple: boolean;
  noOnePerCategory: boolean;
  noIgnored: string;
  noBuffer: number;
  noMax: number;
}

export interface LiveRes {
  year: string;
  base: string;
}

export interface Status {
  visibleInCalendar: boolean;
  registrationOpen: boolean;
  registrationOpenJoined: boolean;
  text: string;
  id: number;
}
export interface Category {
  uuid: string;
  id: string;
  dances: Dance[];
  entriesCount: number;
  estimatedTime: number;
  name: string;
  shortName: string;
  additionalDescription: string;
  blockUuid: string;
  class: string;
  ageCategory: AgeCategoryType;
  block: Block;
  competitionType: CompetitionType;
  explicitStartTime: string;
  startsAt: string;
  sortingString: string;
  sortingStringHours: string;
  style: StyleType;
  subtype: CategorySubtypeType;
  registration: RegistrationInfoType;
  rankingSystem: RankingSystemType;
  wdsfId: string;
  dseId?: string;
  idMd?: string;
  groupId?: string;
}
export interface CompetitionType {
  id: string;
  name: string;
}
export interface AgeCategoryType {
  id: string;
  name: string;
  class: string;
  style: string;
}

export interface CategorySubtypeType {
  dse: boolean;
  wdsf: boolean;
}
export interface RegistrationInfoType {
  open: boolean;
  checkinOpen: boolean;
  checkinStartTime: string;
  checkinFinishTime: string;
}

interface TimetableState {
  timetable: TimetableItem[];
  timetableDetails: TimetableDetails[];
  activeItem: TimetableItem | null;
  activeCompetition: Competition | null;
  monitorDetails: TimetableDetails[];
  monitorDetailsReceiveOrder: string[]; // Array of ttUuids in order they were received
  completedHeats: string[]; // Array of heat UUIDs
  selectedParticipants: Record<string, string[]>; // heatUuid -> array of participant keys
  warningParticipants: Record<string, string[]>; // heatUuid -> array of participant keys
  resultsPresentedRounds: string[]; // Array of ttUuids where results have been presented
  roundSelectedParticipants: Record<string, string[]>; // ttUuid -> array of participant keys (for table/round-level selection)
  roundWarningParticipants: Record<string, string[]>; // ttUuid -> array of participant keys (for table/round-level selection)
  resultsPresentationSelected: Record<string, string[]>; // ttUuid -> array of participant keys (for results presentation in final)
  manuallyCompletedTTItems: string[]; // Array of ttUuids manually marked as completed (for non-round items)
}
const initialState: TimetableState = {
  timetable: [],
  timetableDetails: [],
  monitorDetails: [],
  monitorDetailsReceiveOrder: [],
  activeItem: null,
  activeCompetition: null,
  completedHeats: [],
  selectedParticipants: {},
  warningParticipants: {},
  resultsPresentedRounds: [],
  roundSelectedParticipants: {},
  roundWarningParticipants: {},
  resultsPresentationSelected: {},
  manuallyCompletedTTItems: []
};

const timetableSlice = createSlice({
  name: 'timetable',
  initialState,
  reducers: {
    setTimetable(state, action: PayloadAction<TimetableItem[]>) {
      state.timetable = action.payload;
    },
    setActiveItem(state, action: PayloadAction<TimetableItem>) {
      state.activeItem = action.payload;
    },
    setActiveCompetition(state, action: PayloadAction<Competition>) {
      state.activeCompetition = action.payload;
    },
    addTimetableDetails(state, action: PayloadAction<TimetableDetails>) {
      const newDetails = action.payload;
      if (state.timetableDetails === undefined) {
        state.timetableDetails = [];
      }
      // Check if details for this ttUuid already exist
      const existingIndex = state.timetableDetails.findIndex(
        (detail: TimetableDetails) => detail.ttUuid === newDetails.ttUuid
      );
      if (existingIndex !== -1) {
        state.timetableDetails[existingIndex] = newDetails;
      } else {
        state.timetableDetails.push(newDetails);
      }
    },
    addMonitorDetails(state, action: PayloadAction<TimetableDetails>) {
      const newDetails = { ...action.payload, receivedAt: Date.now() };
      if (state.monitorDetails === undefined) {
        state.monitorDetails = [];
      }
      if (state.monitorDetailsReceiveOrder === undefined) {
        state.monitorDetailsReceiveOrder = [];
      }
      const existingIndex = state.monitorDetails.findIndex(
        (detail: TimetableDetails) => detail.ttUuid === newDetails.ttUuid
      );
      if (existingIndex !== -1) {
        // Update existing details, preserve or update position in receive order
        state.monitorDetails[existingIndex] = newDetails;
        // Move to end of receive order (most recent)
        state.monitorDetailsReceiveOrder = state.monitorDetailsReceiveOrder.filter(
          (uuid) => uuid !== newDetails.ttUuid
        );
        state.monitorDetailsReceiveOrder.push(newDetails.ttUuid);
      } else {
        // Add new details
        state.monitorDetails.push(newDetails);
        state.monitorDetailsReceiveOrder.push(newDetails.ttUuid);
      }
    },
    clearMonitorDetails(state) {
      state.monitorDetails = [];
      state.monitorDetailsReceiveOrder = [];
    },
    clearTimetableData(state) {
      state.timetable = [];
      state.timetableDetails = [];
      state.activeItem = null;
      state.monitorDetails = [];
      state.monitorDetailsReceiveOrder = [];
      state.completedHeats = [];
      state.selectedParticipants = {};
      state.warningParticipants = {};
      state.resultsPresentedRounds = [];
      state.roundSelectedParticipants = {};
      state.roundWarningParticipants = {};
      state.resultsPresentationSelected = {};
      state.manuallyCompletedTTItems = [];
    },
    toggleHeatCompletion(state, action: PayloadAction<string>) {
      const heatUuid = action.payload;
      // Initialize if undefined (for persisted state compatibility)
      if (!state.completedHeats) {
        state.completedHeats = [];
      }
      const index = state.completedHeats.indexOf(heatUuid);
      if (index !== -1) {
        state.completedHeats.splice(index, 1);
      } else {
        state.completedHeats.push(heatUuid);
      }
    },
    toggleParticipantSelection(
      state,
      action: PayloadAction<{ heatUuid: string; participantKey: string }>
    ) {
      const { heatUuid, participantKey } = action.payload;
      // Initialize if undefined (for persisted state compatibility)
      if (!state.selectedParticipants) {
        state.selectedParticipants = {};
      }
      if (!state.selectedParticipants[heatUuid]) {
        state.selectedParticipants[heatUuid] = [];
      }
      const index = state.selectedParticipants[heatUuid].indexOf(participantKey);
      if (index !== -1) {
        state.selectedParticipants[heatUuid].splice(index, 1);
      } else {
        state.selectedParticipants[heatUuid].push(participantKey);
      }
    },
    toggleParticipantWarning(
      state,
      action: PayloadAction<{ heatUuid: string; participantKey: string }>
    ) {
      const { heatUuid, participantKey } = action.payload;
      // Initialize if undefined (for persisted state compatibility)
      if (!state.warningParticipants) {
        state.warningParticipants = {};
      }
      if (!state.selectedParticipants) {
        state.selectedParticipants = {};
      }
      if (!state.warningParticipants[heatUuid]) {
        state.warningParticipants[heatUuid] = [];
      }
      const index = state.warningParticipants[heatUuid].indexOf(participantKey);
      if (index !== -1) {
        state.warningParticipants[heatUuid].splice(index, 1);
      } else {
        state.warningParticipants[heatUuid].push(participantKey);
        // Remove from selected if present
        if (state.selectedParticipants[heatUuid]) {
          const selectedIndex = state.selectedParticipants[heatUuid].indexOf(participantKey);
          if (selectedIndex !== -1) {
            state.selectedParticipants[heatUuid].splice(selectedIndex, 1);
          }
        }
      }
    },
    toggleResultsPresented(state, action: PayloadAction<string>) {
      const ttUuid = action.payload;
      // Initialize if undefined (for persisted state compatibility)
      if (!state.resultsPresentedRounds) {
        state.resultsPresentedRounds = [];
      }
      const index = state.resultsPresentedRounds.indexOf(ttUuid);
      if (index !== -1) {
        state.resultsPresentedRounds.splice(index, 1);
      } else {
        state.resultsPresentedRounds.push(ttUuid);
      }
    },
    toggleRoundParticipantSelection(
      state,
      action: PayloadAction<{ ttUuid: string; participantKey: string }>
    ) {
      const { ttUuid, participantKey } = action.payload;
      // Initialize if undefined (for persisted state compatibility)
      if (!state.roundSelectedParticipants) {
        state.roundSelectedParticipants = {};
      }
      if (!state.roundSelectedParticipants[ttUuid]) {
        state.roundSelectedParticipants[ttUuid] = [];
      }
      const index = state.roundSelectedParticipants[ttUuid].indexOf(participantKey);
      if (index !== -1) {
        state.roundSelectedParticipants[ttUuid].splice(index, 1);
      } else {
        state.roundSelectedParticipants[ttUuid].push(participantKey);
      }
    },
    toggleRoundParticipantWarning(
      state,
      action: PayloadAction<{ ttUuid: string; participantKey: string }>
    ) {
      const { ttUuid, participantKey } = action.payload;
      // Initialize if undefined (for persisted state compatibility)
      if (!state.roundWarningParticipants) {
        state.roundWarningParticipants = {};
      }
      if (!state.roundSelectedParticipants) {
        state.roundSelectedParticipants = {};
      }
      if (!state.roundWarningParticipants[ttUuid]) {
        state.roundWarningParticipants[ttUuid] = [];
      }
      const index = state.roundWarningParticipants[ttUuid].indexOf(participantKey);
      if (index !== -1) {
        state.roundWarningParticipants[ttUuid].splice(index, 1);
      } else {
        state.roundWarningParticipants[ttUuid].push(participantKey);
        // Remove from selected if present
        if (state.roundSelectedParticipants[ttUuid]) {
          const selectedIndex = state.roundSelectedParticipants[ttUuid].indexOf(participantKey);
          if (selectedIndex !== -1) {
            state.roundSelectedParticipants[ttUuid].splice(selectedIndex, 1);
          }
        }
      }
    },
    toggleResultsPresentationParticipant(
      state,
      action: PayloadAction<{ ttUuid: string; participantKey: string; totalParticipants: number }>
    ) {
      const { ttUuid, participantKey, totalParticipants } = action.payload;
      // Initialize if undefined
      if (!state.resultsPresentationSelected) {
        state.resultsPresentationSelected = {};
      }
      if (!state.resultsPresentationSelected[ttUuid]) {
        state.resultsPresentationSelected[ttUuid] = [];
      }
      
      const index = state.resultsPresentationSelected[ttUuid].indexOf(participantKey);
      if (index !== -1) {
        state.resultsPresentationSelected[ttUuid].splice(index, 1);
      } else {
        state.resultsPresentationSelected[ttUuid].push(participantKey);
      }
      
      // Auto-mark as presented if all participants are selected
      if (state.resultsPresentationSelected[ttUuid].length === totalParticipants) {
        if (!state.resultsPresentedRounds) {
          state.resultsPresentedRounds = [];
        }
        if (!state.resultsPresentedRounds.includes(ttUuid)) {
          state.resultsPresentedRounds.push(ttUuid);
        }
      }
    },
    toggleManualTTItemCompletion(
      state,
      action: PayloadAction<string> // ttUuid
    ) {
      const ttUuid = action.payload;
      // Initialize if undefined
      if (!state.manuallyCompletedTTItems) {
        state.manuallyCompletedTTItems = [];
      }
      const index = state.manuallyCompletedTTItems.indexOf(ttUuid);
      if (index !== -1) {
        state.manuallyCompletedTTItems.splice(index, 1);
      } else {
        state.manuallyCompletedTTItems.push(ttUuid);
      }
    }
  }
});

export const {
  setTimetable,
  setActiveItem,
  addTimetableDetails,
  clearTimetableData,
  clearMonitorDetails,
  setActiveCompetition,
  addMonitorDetails,
  toggleHeatCompletion,
  toggleParticipantSelection,
  toggleParticipantWarning,
  toggleResultsPresented,
  toggleRoundParticipantSelection,
  toggleRoundParticipantWarning,
  toggleResultsPresentationParticipant,
  toggleManualTTItemCompletion
} = timetableSlice.actions;
export const selectTimetable = (state: RootState) => {
  return state.timetable?.timetable;
};
/**
 * Get timetable filtered by selected hall.
 * If selectedHall is 'all' or not set, returns all items.
 */
export const selectTimetableForHall = (state: RootState) => {
  const selectedHall = state.settings.selectedHall;
  const timetable = state.timetable?.timetable;
  if (!selectedHall || selectedHall === 'all') {
    return timetable;
  }
  // Fallback to all items if selected hall exceeds competition's hall count
  const hallsNumber = state.timetable?.activeCompetition?.hallsNumber || 1;
  if (typeof selectedHall === 'number' && selectedHall > hallsNumber) {
    return timetable;
  }
  return timetable?.filter(
    (item: TimetableItem) => (item.hall || 1) === selectedHall,
  );
};
export const selectActiveItem = (state: RootState) =>
  state.timetable?.activeItem;
export const selectActiveCompetition = (state: RootState) =>
  state.timetable?.activeCompetition;
export const selectTimetableDetailsByUuid = (
  state: RootState,
  ttUuid: string
) =>
  state.timetable?.timetableDetails?.find(
    (detail: TimetableDetails) => detail.ttUuid === ttUuid
  );
export const selectTimetableDetailsForItems = (
  state: RootState,
  items: TimetableItem[]
): (TimetableDetails | undefined)[] =>
  items?.map((item) =>
    state.timetable?.timetableDetails?.find(
      (detail: TimetableDetails) => detail.ttUuid === item.ttUuid
    )
  );
export const selectMonitorDetailsByUuid = (state: RootState, ttUuid: string) =>
  state.timetable?.monitorDetails?.find(
    (detail: TimetableDetails) => detail.ttUuid === ttUuid
  );
export const selectMonitorDetailsForItems = (
  state: RootState,
  items: TimetableItem[]
): (TimetableDetails | undefined)[] =>
  items?.map((item) =>
    state.timetable?.monitorDetails?.find(
      (detail: TimetableDetails) => detail.ttUuid === item.ttUuid
    )
  );
export const selectLast4MonitorDetailsInReceiveOrder = (
  state: RootState
): TimetableDetails[] => {
  const receiveOrder = state.timetable?.monitorDetailsReceiveOrder || [];
  const monitorDetails = state.timetable?.monitorDetails || [];
  
  // Get last 4 ttUuids from receive order
  const last4Uuids = receiveOrder.slice(-4);
  
  // Map to actual details objects
  return last4Uuids
    .map((uuid) =>
      monitorDetails.find((detail) => detail.ttUuid === uuid)
    )
    .filter((detail): detail is TimetableDetails => detail !== undefined);
};

export const selectCompletedHeats = (state: RootState) =>
  state.timetable?.completedHeats || [];

export const selectIsHeatCompleted = (state: RootState, heatUuid: string) =>
  state.timetable?.completedHeats?.includes(heatUuid) || false;

export const selectSelectedParticipantsForHeat = (
  state: RootState,
  heatUuid: string
) => state.timetable?.selectedParticipants?.[heatUuid] || [];

export const selectWarningParticipantsForHeat = (
  state: RootState,
  heatUuid: string
) => state.timetable?.warningParticipants?.[heatUuid] || [];

export const selectIsParticipantSelected = (
  state: RootState,
  heatUuid: string,
  participantKey: string
) =>
  state.timetable?.selectedParticipants?.[heatUuid]?.includes(participantKey) ||
  false;

export const selectIsParticipantWarning = (
  state: RootState,
  heatUuid: string,
  participantKey: string
) =>
  state.timetable?.warningParticipants?.[heatUuid]?.includes(participantKey) ||
  false;

export const selectAreAllHeatsCompleted = (
  state: RootState,
  ttUuid: string
) => {
  // Check if manually marked as completed first
  if (state.timetable?.manuallyCompletedTTItems?.includes(ttUuid)) {
    return true;
  }
  
  const details = state.timetable?.timetableDetails?.find(
    (detail: TimetableDetails) => detail.ttUuid === ttUuid
  ) || state.timetable?.monitorDetails?.find(
    (detail: TimetableDetails) => detail.ttUuid === ttUuid
  );
  // console.log(details);
  if (!details || !details.heats || details.heats.length === 0) {
    return false;
  }
  
  const completedHeats = state.timetable?.completedHeats || [];
  const allHeatUuids = details.heats.map(heat => heat.uuid);
  
  return allHeatUuids.length > 0 && allHeatUuids.every(heatUuid => 
    completedHeats.includes(heatUuid)
  );
};

export const selectIsResultsPresented = (
  state: RootState,
  ttUuid: string
) => state.timetable?.resultsPresentedRounds?.includes(ttUuid) || false;

export const selectHasResults = (
  state: RootState,
  ttUuid: string
) => {
  const details = state.timetable?.timetableDetails?.find(
    (detail: TimetableDetails) => detail.ttUuid === ttUuid
  ) || state.timetable?.monitorDetails?.find(
    (detail: TimetableDetails) => detail.ttUuid === ttUuid
  );
  
  return details?.results !== null && details?.results !== undefined;
};

export const selectIsFinalRound = (
  state: RootState,
  ttUuid: string
) => {
  const details = state.timetable?.timetableDetails?.find(
    (detail: TimetableDetails) => detail.ttUuid === ttUuid
  ) || state.timetable?.monitorDetails?.find(
    (detail: TimetableDetails) => detail.ttUuid === ttUuid
  );
  
  return details?.currentRound?.isFinal || false;
};

export const selectRoundSelectedParticipants = (
  state: RootState,
  ttUuid: string
) => state.timetable?.roundSelectedParticipants?.[ttUuid] || [];

export const selectRoundWarningParticipants = (
  state: RootState,
  ttUuid: string
) => state.timetable?.roundWarningParticipants?.[ttUuid] || [];

export const selectIsRoundParticipantSelected = (
  state: RootState,
  ttUuid: string,
  participantKey: string
) =>
  state.timetable?.roundSelectedParticipants?.[ttUuid]?.includes(participantKey) ||
  false;

export const selectIsRoundParticipantWarning = (
  state: RootState,
  ttUuid: string,
  participantKey: string
) =>
  state.timetable?.roundWarningParticipants?.[ttUuid]?.includes(participantKey) ||
  false;

export const selectResultsPresentationSelected = (
  state: RootState,
  ttUuid: string
) => state.timetable?.resultsPresentationSelected?.[ttUuid] || [];

export const selectIsResultsPresentationParticipantSelected = (
  state: RootState,
  ttUuid: string,
  participantKey: string
) =>
  state.timetable?.resultsPresentationSelected?.[ttUuid]?.includes(participantKey) ||
  false;

export const selectIsManuallyCompleted = (
  state: RootState,
  ttUuid: string
) => state.timetable?.manuallyCompletedTTItems?.includes(ttUuid) || false;

export default timetableSlice.reducer;
